import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import logoimg from "../assets/images/logo.png";
import EnrollForm from "../pages/EnrollForm";
import { Helmet } from "react-helmet-async";
/* ============================================================
   VIDEO HELPER UTILITIES
============================================================ */

const isYouTubeId = (val) => {
  if (!val) return false;
  return /^[A-Za-z0-9_-]{11}$/.test(String(val).trim());
};

const isYouTubeUrl = (val) => {
  if (!val) return false;
  return /youtube\.com|youtu\.be/.test(String(val));
};

const extractYouTubeId = (val) => {
  if (!val) return null;
  const s = String(val).trim();
  if (isYouTubeId(s)) return s;
  const m = s.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const isVideoFile = (val) => {
  if (!val) return false;
  return /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(String(val).trim());
};

/**
 * Resolve the video source from a lesson object.
 * Returns { type: 'youtube'|'local'|'none', src: string|null }
 */
const resolveVideo = (lesson) => {
  if (!lesson) return { type: "none", src: null };

  const url = String(
    lesson.videoUrl || lesson.video_url || lesson.url || "",
  ).trim();
  const vid = String(
    lesson.videoId || lesson.video_id || lesson.video || "",
  ).trim();
  const file = String(
    lesson.videoFile || lesson.video_file || lesson.filename || "",
  ).trim();

  // 1. Explicit local filename field
  if (isVideoFile(file))
    return {
      type: "local",
      src: `https://swiftintelli.com/api/uploads/videos/${file}`,
    };

  // 2. YouTube URL
  if (isYouTubeUrl(url)) {
    const id = extractYouTubeId(url);
    if (id) return { type: "youtube", src: id };
  }

  // 3. Non-YT URL (direct path or full URL)
  if (url) {
    return {
      type: "local",
      src: url.startsWith("http") ? url : `https://swiftintelli.com/api/${url}`,
    };
  }

  // 4. YouTube ID string
  if (isYouTubeId(vid)) return { type: "youtube", src: vid };

  // 5. YouTube URL inside videoId field
  if (isYouTubeUrl(vid)) {
    const id = extractYouTubeId(vid);
    if (id) return { type: "youtube", src: id };
  }

  // 6. Video filename inside videoId field
  if (isVideoFile(vid))
    return {
      type: "local",
      src: `https://swiftintelli.com/api/uploads/videos/${vid}`,
    };

  // 7. Numeric DB id → served by PHP endpoint
  const num = Number(vid);
  if (vid && !isNaN(num) && num > 0)
    return {
      type: "local",
      src: `https://swiftintelli.com/api/getVideo.php?id=${num}`,
    };

  return { type: "none", src: null };
};

/* ============================================================
   INLINE VIDEO PLAYER
   - No modal. Lives inside the curriculum section.
   - Resets whenever the active lesson changes.
============================================================ */

/* ============================================================
   MAIN COMPONENT
============================================================ */
export default function CourseDetail() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [openMods, setOpenMods] = useState([0]);
  const [enrolled, setEnrolled] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);

  // Holds the full lesson object currently loaded in the player

  /* ── helpers ── */
  const toArray = (val) => {
    if (!val) return [];

    if (Array.isArray(val)) return val;

    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
      } catch {}

      return val
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }

    return [];
  };

  const toStars = (val) => {
    const n = parseInt(val, 10);
    return isNaN(n) ? 5 : Math.min(5, Math.max(1, n));
  };

  /* ── data fetch ── */
  useEffect(() => {
    if (!slug) return;
    fetch(`https://swiftintelli.com/api/getCourse.php?slug=${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.json();
      })
      .then((data) => {
        setCourse(data.error ? null : data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  /* ── enroll submit ── */

  const goBack = () => {
    navigate("/");
    window.scrollTo({ top: 0 });
  };
  const toggle = (i) =>
    setOpenMods((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));

  /* ── select a lesson (scroll to player) ── */

  if (loading)
    return <h2 style={{ textAlign: "center", marginTop: 80 }}>Loading...</h2>;
  if (!course)
    return (
      <h2 style={{ textAlign: "center", marginTop: 80 }}>
        Course not found ❌
      </h2>
    );

  const tags = toArray(course.tags);
  const learn = toArray(course.learn);
  const includes = toArray(course.includes);
  const curriculum = toArray(course.curriculum);
  const reviews = toArray(course.reviews);
  const requirements = toArray(course.requirements);

  const validIncludes = includes.filter(
    (item) =>
      item &&
      item.icon &&
      item.value &&
      String(item.value).trim() &&
      item.label &&
      String(item.label).trim(),
  );
  const dynamicIncludes = [
    course.video_hours && {
      icon: "🎬",
      value: `${course.video_hours}+`,
      label: "Hours of Video",
    },
    course.labs && {
      icon: "🧪",
      value: String(course.labs),
      label: "Hands-on Labs",
    },
    course.assignments && {
      icon: "📝",
      value: String(course.assignments),
      label: "Assignments",
    },
    course.live_sessions && {
      icon: "💬",
      value: String(course.live_sessions),
      label: "Live Sessions",
    },
    { icon: "🏆", value: "1", label: "Certificate" },
    { icon: "♾️", value: "∞", label: "Lifetime Access" },
    course.duration && {
      icon: "⏱",
      value: course.duration,
      label: "Course Duration",
    },
    course.projects && {
      icon: "🚀",
      value: String(course.projects),
      label: "Real Projects",
    },
  ].filter(Boolean);
  const displayIncludes =
    validIncludes.length >= 2 ? validIncludes : dynamicIncludes;

  const getLessonIcon = (lesson) => {
    if (lesson.type === "quiz") return "▪";
    if (lesson.type === "lab") return "✏";
    if (lesson.type === "doc") return "▪";
    return "▶";
  };

  // First lesson as default
  const firstLesson = curriculum[0]?.lessons?.[0] || null;

const courseName =
  (
    course?.name ||
    course?.title ||
    ""
  ).toLowerCase();

const isAI =
  courseName.includes("ai") ||
  courseName.includes("machine");

const isPrompt =
  courseName.includes("prompt");

const seoTitle = isAI

  ? "AI ML Course with Hands-On Training & Live Projects | SwiftIntelli"

  : "Prompt Engineering Course with Hands-On AI Training | SwiftIntelli";

const seoDescription = isAI

  ? "Master Artificial Intelligence & Machine Learning at SwiftIntelli with live projects, Python training, and industry-focused curriculum. Get job-ready. Enroll now!"

  : "Master prompt engineering at SwiftIntelli with hands-on AI tools, real-world projects, and expert-led training. Build job-ready skills and get certified. Join now!";

const seoKeywords = isAI

  ? `
      AI ML Course,
      Artificial Intelligence Training,
      Machine Learning,
      Python AI,
      AI Bootcamp,
      SwiftIntelli,
      Online AI Training,
      AI Certification
    `

  : `
      Prompt Engineering,
      AI Prompt Training,
      ChatGPT Course,
      Generative AI,
      LLM Training,
      SwiftIntelli,
      Prompt Engineering Certification
    `;
  return (
    <>
     <Helmet>

  <title>
    {seoTitle}
  </title>

  <meta
    name="description"
    content={seoDescription}
  />

  <meta
    name="keywords"
    content={seoKeywords}
  />

  {/* OPEN GRAPH */}

  <meta
    property="og:title"
    content={seoTitle}
  />

  <meta
    property="og:description"
    content={seoDescription}
  />

  <meta
    property="og:image"
    content={`https://swiftintelli.com/uploads/${course?.image}`}
  />

  <meta
    property="og:url"
    content={window.location.href}
  />

  <meta
    property="og:type"
    content="website"
  />

  {/* TWITTER */}

  <meta
    name="twitter:card"
    content="summary_large_image"
  />

  <meta
    name="twitter:title"
    content={seoTitle}
  />

  <meta
    name="twitter:description"
    content={seoDescription}
  />

  <meta
    name="twitter:image"
    content={`https://swiftintelli.com/uploads/${course?.image}`}
  />

  {/* CANONICAL */}

  <link
    rel="canonical"
    href={window.location.href}
  />

</Helmet>


      {/* NAV */}
      <nav className={`cd-nav ${scrolled ? "scrolled" : ""}`}>
        <div className="cd-nav-logo" onClick={goBack}>
          <img src={logoimg} alt="logo" className="si-logo" />
        </div>
        <button className="cd-nav-back" onClick={goBack}>
          ⬅ Back to Courses
        </button>
        <div className="cd-nav-right">
          <button
            className={`cd-nav-enroll ${enrolled ? "ok" : ""}`}
            onClick={() => setShowEnrollModal(true)}
          >
            {enrolled ? "✓ Enrolled!" : "Enroll Now"}
          </button>
        </div>
      </nav>

      {/* HERO */}
      <div className="cd-hero">
        <div className="cd-hero-grid" />
        <div className="cd-hero-orb cd-orb1" />
        <div className="cd-hero-orb cd-orb2" />
        <div className="cd-hero-inner">
          <div className="cd-crumb">
            <span onClick={goBack}>Home</span>
            <span className="cd-crumb-sep">›</span>
            <span onClick={goBack}>Courses</span>
            <span className="cd-crumb-sep">›</span>
            <span style={{ color: "rgba(255,255,255,0.8)" }}>
              {course.category || "Course"}
            </span>
          </div>
          <div className="cd-live">
            <div className="cd-live-dot" />
            {course.type === "live" ? "Live — Enrolling Now" : "Coming Soon"}
          </div>
          <h1 className="cd-title">{course.title}</h1>
          <p className="cd-desc">{course.description}</p>
          <div className="cd-meta-row">
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span className="cd-stars">★★★★★</span>
              <span className="cd-rating-val">{course.rating || "4.5"}</span>
              <span className="cd-rating-ct">({reviews.length} reviews)</span>
            </div>
            <div className="cd-mi">
              👥 <b>{course.students || "500+"}</b> students enrolled
            </div>
            <div className="cd-mi">
              🕐 Updated {course.updated_at || "Recently"}
            </div>
          </div>
          <div className="cd-meta-row">
            <div className="cd-mi">
              ⏱ <b>{course.duration}</b>
            </div>
            <div className="cd-mi">
              📚 <b>{course.category}</b>
            </div>
            <div className="cd-mi">
              🌐 <b>{course.mode || "Live + Recorded"}</b>
            </div>
            <div className="cd-mi">
              🏆 <b>Certificate</b>
            </div>
            <div className="cd-mi">💬 {course.language || "English"}</div>
          </div>
          {(tags.length > 0 || course.category) && (
            <div className="cd-tags">
              {(tags.length > 0 ? tags : [course.category].filter(Boolean)).map(
                (t, i) => (
                  <span key={i} className="cd-tag">
                    {t}
                  </span>
                ),
              )}
            </div>
          )}
        </div>
      </div>

      {/* CONTENT */}
      <div className="cd-content">
        <div className="cd-left">
          {/* WHAT YOU'LL LEARN */}
          <div className="cd-sec">
            <h2 className="cd-stitle">What You'll Learn</h2>
            {learn.length > 0 ? (
              <div className="cd-learn-grid">
                {learn.map((item, i) => {
                  const text =
                    typeof item === "string"
                      ? item
                      : item.text || item.title || JSON.stringify(item);
                  return (
                    <div key={i} className="cd-learn-item">
                      <span className="cd-learn-tick">✓</span>
                      <span className="cd-learn-text">{text}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p>No data available</p>
            )}
          </div>

          {/* THIS COURSE INCLUDES */}
          <div className="cd-sec">
            <h2 className="cd-stitle">This Course Includes</h2>
            <div className="cd-incs">
              {(displayIncludes.length > 0
                ? displayIncludes
                : [
                    { icon: "🎬", value: "20+", label: "Hours of Video" },
                    { icon: "🏆", value: "1", label: "Certificate" },
                    { icon: "👨‍🏫", value: "Live", label: "Live Sessions" },
                    { icon: "♾️", value: "∞", label: "Lifetime Access" },
                    { icon: "📝", value: "Yes", label: "Assignments" },
                    { icon: "🚀", value: "5+", label: "Real Projects" },
                  ]
              ).map((item, i) => (
                <div key={i} className="cd-inc">
                  <div className="cd-inc-icon">{item.icon}</div>
                  <div className="cd-inc-val">{item.value}</div>
                  <div className="cd-inc-lbl">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* CURRICULUM */}
          <div className="cd-sec">
            <div className="cd-cur-head">
              <h2 className="cd-stitle">Course Curriculum</h2>

              {course.curriculum_stats && (
                <div className="cd-cur-stats">
                  <span>
                    {course.curriculum_stats.modules} modules ·{" "}
                    {course.curriculum_stats.lessons} lessons
                  </span>
                </div>
              )}
            </div>

            {curriculum.length > 0 ? (
              curriculum.map((mod, i) => {
                const lessons = toArray(mod.lessons);

                return (
                  <div key={i} className="cd-module">
                    {/* MODULE HEADER */}
                    <div className="cd-mhdr" onClick={() => toggle(i)}>
                      <div className="cd-mnum">
                        {String(i + 1).padStart(2, "0")}
                      </div>

                      <div className="cd-minfo">
                        <div className="cd-mtitle">
                          {mod.module || mod.title}
                        </div>
                        <div className="cd-mmeta">{lessons.length} lessons</div>
                      </div>

                      <span
                        className={`cd-marrow ${openMods.includes(i) ? "open" : ""}`}
                      >
                        {openMods.includes(i) ? "−" : "+"}
                      </span>
                    </div>

                    {/* LESSON LIST */}
                    {openMods.includes(i) && (
                      <div className="cd-mlessons">
                        {lessons.map((lesson, j) => (
                          <div key={j} className="cd-lesson">
                            {/* CONTENT */}
                            <div className="cd-lname">
                              {/* TITLE */}
                              <div style={{ fontWeight: 500 }}>
                                {lesson.title}
                              </div>

                              {/* TOPICS */}

                              {toArray(lesson.topics).length > 0 && (
                                <ul className="cd-topics-list">
                                  {toArray(lesson.topics).map((t, i) => (
                                    <li key={i}>{t}</li>
                                  ))}
                                </ul>
                              )}

                              {/* SUBTOPICS */}
                              {toArray(lesson.subtopics).length > 0 && (
                                <div>
                                  {toArray(lesson.subtopics).join(", ")}
                                </div>
                              )}

                              {/* ASSIGNMENT */}
                              {lesson.assignment && (
                                <div>{lesson.assignment}</div>
                              )}

                              {/* EXAMPLE */}
                              {lesson.example && <div>{lesson.example}</div>}
                            </div>

                            {/* FREE BADGE */}
                            {lesson.free && (
                              <span className="cd-lbadge">FREE</span>
                            )}

                            {/* DURATION */}
                            <span className="cd-ldur">{lesson.duration}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p>No curriculum available</p>
            )}
          </div>

          {/* REQUIREMENTS */}
          <div className="cd-sec">
            <h2 className="cd-stitle">Requirements</h2>
            <ul className="cd-req-list">
              {(requirements.length > 0
                ? requirements
                : [
                    "Basic Python programming knowledge (variables, loops, functions)",
                    "High school-level mathematics (algebra & statistics are helpful)",
                    "A laptop or desktop — Windows, Mac, or Linux all supported",
                    "Stable internet connection for live sessions",
                    "Commitment to complete weekly assignments and hands-on labs",
                  ]
              ).map((req, i) => {
                const text =
                  typeof req === "string"
                    ? req
                    : req.text || req.title || JSON.stringify(req);
                return (
                  <li key={i}>
                    <span className="cd-req-dot" />
                    <span>{text}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* INSTRUCTOR */}
          <div className="cd-sec">
            <h2 className="cd-stitle">Your Instructor</h2>
            <div className="cd-inst">
              <div className="cd-iavt">
                {(() => {
                  const iname = course.instructor?.name || "";
                  const parts = iname.trim().split(" ").filter(Boolean);
                  if (parts.length >= 2)
                    return (
                      parts[0][0] + parts[parts.length - 1][0]
                    ).toUpperCase();
                  return parts[0]?.[0]?.toUpperCase() || "A";
                })()}
              </div>
              <div className="cd-iinfo">
                <div className="cd-iname">
                  {course.instructor?.name || "Instructor"}
                </div>
                <div className="cd-irole">
                  {course.instructor?.role || ""}
                  {course.instructor?.company
                    ? ` · ${course.instructor.company}`
                    : ""}
                </div>
                <div className="cd-istats">
                  {course.instructor?.rating && (
                    <>
                      <div className="cd-istat">
                        <span style={{ color: "#facc15" }}>★</span>
                        <span className="cd-istat-val">
                          {course.instructor.rating}
                        </span>
                        <span>Rating</span>
                      </div>
                      <span className="cd-istat-sep">|</span>
                    </>
                  )}
                  {course.instructor?.students && (
                    <>
                      <div className="cd-istat">
                        <span className="cd-istat-val">
                          {course.instructor.students}
                        </span>
                        <span>Students</span>
                      </div>
                      <span className="cd-istat-sep">|</span>
                    </>
                  )}
                  {course.instructor?.courses && (
                    <>
                      <div className="cd-istat">
                        <span className="cd-istat-val">
                          {course.instructor.courses}
                        </span>
                        <span>Courses</span>
                      </div>
                      <span className="cd-istat-sep">|</span>
                    </>
                  )}
                  {course.instructor?.experience && (
                    <div className="cd-istat">
                      <span className="cd-istat-val">
                        {course.instructor.experience}
                      </span>
                      <span>Experience</span>
                    </div>
                  )}
                </div>
                <p className="cd-ibio">
                  {course.instructor?.bio || "No bio available"}
                </p>
              </div>
            </div>
          </div>

          {/* REVIEWS */}
          <div className="cd-sec">
            <h2 className="cd-stitle">Student Reviews</h2>
            {reviews.length > 0 &&
              (() => {
                const avg =
                  reviews.reduce((s, r) => s + toStars(r.rating), 0) /
                  reviews.length;
                const dist = [5, 4, 3, 2, 1].map((star) => ({
                  star,
                  pct: Math.round(
                    (reviews.filter((r) => toStars(r.rating) === star).length /
                      reviews.length) *
                      100,
                  ),
                }));
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 32,
                      background: "#f8f9fc",
                      border: "1px solid #e8eaf0",
                      borderRadius: 12,
                      padding: "20px 24px",
                      marginBottom: 20,
                    }}
                  >
                    <div
                      style={{
                        textAlign: "center",
                        flexShrink: 0,
                        minWidth: 90,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 56,
                          fontWeight: 800,
                          color: "#2563eb",
                          lineHeight: 1,
                          marginBottom: 6,
                        }}
                      >
                        {avg.toFixed(1)}
                      </div>
                      <div
                        style={{
                          color: "#facc15",
                          fontSize: 20,
                          letterSpacing: 2,
                          marginBottom: 4,
                        }}
                      >
                        {"★".repeat(Math.round(avg))}
                      </div>
                      <div style={{ fontSize: 12, color: "#9ca3af" }}>
                        Course Rating
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 7,
                      }}
                    >
                      {dist.map(({ star, pct }) => (
                        <div
                          key={star}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 12,
                            color: "#6b7280",
                          }}
                        >
                          <span
                            style={{
                              width: 14,
                              textAlign: "right",
                              flexShrink: 0,
                            }}
                          >
                            {star}
                          </span>
                          <span style={{ color: "#facc15", flexShrink: 0 }}>
                            ★
                          </span>
                          <div
                            style={{
                              flex: 1,
                              height: 8,
                              background: "#e8eaf0",
                              borderRadius: 4,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${pct}%`,
                                height: "100%",
                                background: "#f59e0b",
                                borderRadius: 4,
                              }}
                            />
                          </div>
                          <span
                            style={{
                              width: 32,
                              textAlign: "right",
                              flexShrink: 0,
                              color: "#9ca3af",
                            }}
                          >
                            {pct}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
              {reviews.length > 0 ? (
                reviews.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#fff",
                      border: "1px solid #e8eaf0",
                      borderRadius: 12,
                      padding: 18,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          background: "#dbeafe",
                          color: "#2563eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 14,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {r.init ||
                          (r.name ? r.name.charAt(0).toUpperCase() : "A")}
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#1a1d23",
                          }}
                        >
                          {r.name}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#9ca3af",
                            marginTop: 2,
                          }}
                        >
                          {r.role} · {r.date}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        color: "#facc15",
                        fontSize: 14,
                        letterSpacing: 1,
                        marginBottom: 8,
                      }}
                    >
                      {"★".repeat(toStars(r.rating))}
                      <span style={{ color: "#e8eaf0" }}>
                        {"★".repeat(5 - toStars(r.rating))}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#4b5563",
                        lineHeight: 1.65,
                      }}
                    >
                      {r.text}
                    </div>
                  </div>
                ))
              ) : (
                <p
                  style={{ color: "#9ca3af", fontSize: 14, gridColumn: "1/-1" }}
                >
                  No reviews yet
                </p>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT STICKY CARD */}
        <div className="cd-right">
          <div className="cd-sticky">
            <div className="cd-ecard">
              <div className="cd-ethumb">
                <img
                  src={
                    course.image
                      ? `https://swiftintelli.com/api/uploads/${course.image}`
                      : "/default.jpg"
                  }
                  alt={course.title || "Course"}
                  className="cd-ethumb-img"
                  onError={(e) => {
                    e.target.src = "/default.jpg";
                  }}
                />
                <div className="cd-ethumb-overlay" />
              </div>
              <div className="cd-ebody">
                <div className="cd-price">₹{course.price}</div>
                <div className="cd-pnote">⏰ Limited seats available!</div>
                <button
                  className="cd-ebtn"
                  onClick={() => setShowEnrollModal(true)}
                >
                  Enroll Now — Secure Your Seat
                </button>
                {/* <button className="cd-tbtn">Try Free Preview Lessons</button> */}
                <ul className="cd-perks">
                  {(includes.length > 0
                    ? includes.map((inc) => [inc.icon, inc.label])
                    : [
                        ["📚", "Course videos"],
                        ["🏆", "Certificate"],
                        ["👨‍🏫", "Live sessions"],
                        ["♾️", "Lifetime access"],
                      ]
                  ).map(([icon, text], i) => (
                    <li key={i}>
                      <span className="cd-pi">{icon}</span>
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ENROLL MODAL — only for enrollment form, not for video */}
      {showEnrollModal && (
        <div
          className="enroll-modal-overlay"
          onClick={() => setShowEnrollModal(false)}
        >
          <div className="enroll-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="enroll-close"
              onClick={() => setShowEnrollModal(false)}
            >
              ✕
            </button>

            <EnrollForm
              isModal={true}
              onClose={() => setShowEnrollModal(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
